import { useCallback, useEffect, useMemo, useState } from 'react';
import * as authApi from '../api/auth';

const TOKEN_KEY = 'modskill_token';

export const useAuth = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        setUser(null);
        return;
      }

      setLoading(true);
      try {
        const { user: profile } = await authApi.currentUser(token);
        setUser(profile);
      } catch (err) {
        console.warn('Unable to fetch profile', err);
        setToken(null);
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  const authenticate = useCallback(
    async (mode, payload) => {
      setLoading(true);
      setError(null);
      try {
        const apiMethod = mode === 'register' ? authApi.register : authApi.login;
        const data = await apiMethod(payload);

        setToken(data.token);
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
        return data.user;
      } catch (err) {
        setError(err.message || 'Unexpected error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return useMemo(
    () => ({
      user,
      token,
      loading,
      error,
      authenticate,
      logout,
      clearError,
    }),
    [authenticate, clearError, error, loading, logout, token, user]
  );
};
